import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import i18n from 'i18next';

import {
  API_CACHE_KEYS,
  ApiResponseCache,
  clearAllTrainingExercisesCaches,
} from '../redux/api-response-cache';
import {
  exercisesConfigApi,
  resetExerciseConfigApiState,
} from '../redux/api/exerciseConfigAPI';
import { resetExerciseApiState } from '../redux/api/exercisesAPI';
import { programsApi } from '../redux/api/programsAPI';
import { trainingsApi } from '../redux/api/trainingsAPI';
import { translationsApi, useGetLanguages } from '../redux/api/translationsAPI';
import { useGetMyDetails } from '../redux/api/users/usersApi';
import {
  EXERCISE_CONFIG,
  PROGRAMS,
  REDUX_CACHE,
  TRAININGS,
  TRANSLATIONS,
} from '../redux/constants';
import { Language } from '../shared/constants';
import {
  LocalStorageKey,
  LocalStorageService,
} from '../shared/storage/local-storage';
import { ILanguage } from '../types/interfaces/language';
import { getDateFnsLocale } from '../utils/datetime/date-time-utils';
import { LoginUserStatus, useAuth } from './AuthContext';

interface ILanguageContextValue {
  currentLanguage: ILanguage | undefined;
}

const LanguageContext = React.createContext<ILanguageContextValue | undefined>(
  undefined,
);

const defaultLanguageKey = Language.en;

interface ILanguageProviderProps {
  children: React.ReactNode;
}

function LanguageProvider(props: ILanguageProviderProps) {
  const { loginUserStatus } = useAuth();

  const { data: user } = useGetMyDetails({
    skip: loginUserStatus !== LoginUserStatus.loggedIn,
  });
  const { data: languages } = useGetLanguages(undefined, {
    skip: loginUserStatus !== LoginUserStatus.loggedIn || !user,
  });

  const currentLanguage =
    languages?.find((l) => l.id === user?.languageId) ||
    languages?.find((l) => l.key === defaultLanguageKey);
  const [dateAdapterLocale, setDateAdapterLocale] = useState(() =>
    getDateFnsLocale(i18n.language),
  );
  const dispatch = useDispatch();
  /** Avoid redundant i18n.changeLanguage on every /me refetch. */
  const lastResolvedLanguageKeyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const onLanguageChanged = (languageKey: string) => {
      setDateAdapterLocale(getDateFnsLocale(languageKey));
      ApiResponseCache.remove(API_CACHE_KEYS.EXERCISE_OPTIONS);
      ApiResponseCache.remove(API_CACHE_KEYS.AVAILABLE_EXERCISES);
      ApiResponseCache.remove(API_CACHE_KEYS.AVAILABLE_DETAILED_EXERCISES);
      clearAllTrainingExercisesCaches();

      dispatch(
        translationsApi.util.invalidateTags([
          { type: TRANSLATIONS.LANGUAGES, id: REDUX_CACHE.LIST },
        ]),
      );
      dispatch(resetExerciseApiState());
      dispatch(resetExerciseConfigApiState());
      dispatch(
        programsApi.util.invalidateTags([
          { type: PROGRAMS.AVAILABLE, id: REDUX_CACHE.LIST },
          { type: PROGRAMS.EXERCISES, id: REDUX_CACHE.LIST },
        ]),
      );
      dispatch(
        trainingsApi.util.invalidateTags([
          { type: TRAININGS.AVAILABLE, id: REDUX_CACHE.LIST },
          { type: TRAININGS.EXERCISES, id: REDUX_CACHE.LIST },
        ]),
      );
      dispatch(
        exercisesConfigApi.util.invalidateTags([
          { type: EXERCISE_CONFIG.EXERCISE_OPTIONS, id: REDUX_CACHE.LIST },
          { type: EXERCISE_CONFIG.EXERCISE_CATEGORIES, id: REDUX_CACHE.LIST },
        ]),
      );
    };

    i18n.on('languageChanged', onLanguageChanged);
    return () => {
      i18n.off('languageChanged', onLanguageChanged);
    };
  }, [dispatch]);

  useEffect(() => {
    if (!languages || !user) return;

    const language =
      languages?.find((l) => l.id === user?.languageId) ||
      languages?.find((l) => l.key === defaultLanguageKey);
    const nextLanguageKey = language?.key || defaultLanguageKey;

    LocalStorageService.setValue(
      LocalStorageKey.language,
      nextLanguageKey as Language,
    );

    if (i18n.language !== nextLanguageKey) {
      i18n.changeLanguage(nextLanguageKey);
    }

    if (lastResolvedLanguageKeyRef.current !== nextLanguageKey) {
      lastResolvedLanguageKeyRef.current = nextLanguageKey;
    }
  }, [languages, user?.languageId, dispatch]);

  return (
    <LanguageContext.Provider
      value={{
        currentLanguage,
      }}>
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={dateAdapterLocale}>
        {props.children}
      </LocalizationProvider>
    </LanguageContext.Provider>
  );
}

function useLanguage() {
  const context = React.useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within an LanguageProvider');
  }

  return context;
}

export { useLanguage, LanguageProvider };
